/**
 * Enhanced User Management Component
 * Modern, responsive user management dashboard with improved UX
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNotifications } from '../common/NotificationSystem';
import { authService } from '../../services/authService';
import { getQuickFilters } from '../../utils/filterUtils';
import ProfessionalLoader from '../common/ProfessionalLoader';
import UserCreateModal from './UserCreateModal';
import UserEditModal from './UserEditModal';
import UserDetailModal from './UserDetailModal';
import UnifiedHeader from '../common/UnifiedHeader';
import UnifiedStats from '../common/UnifiedStats';
import UnifiedFilters from '../common/UnifiedFilters';
import UnifiedActionPanel from '../common/UnifiedActionPanel';
import UnifiedTable from '../common/UnifiedTable';
import UnifiedEmptyState from '../common/UnifiedEmptyState';
import './EnhancedUserManagement.css';

const EnhancedUserManagement = () => {
  const { showSuccess, showError } = useNotifications();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditUser, setShowEditUser] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showUserDetail, setShowUserDetail] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedAction, setSelectedAction] = useState('');
  const [actionReason, setActionReason] = useState('');
  const [searchParams, setSearchParams] = useState({
    search: '',
    is_approved: '',
    is_staff: '',
    is_superuser: '',
    is_active: '',
    is_email_verified: ''
  });
  const [searchInput, setSearchInput] = useState('');

  // Statistics
  const [stats, setStats] = useState({
    totalUsers: 0,
    pendingUsers: 0,
    approvedUsers: 0,
    staffUsers: 0,
    superUsers: 0,
    activeUsers: 0
  });

  // Load users with enhanced error handling
  const loadUsers = useCallback(async (isSearch = false) => {
    try {
      // Only show loading spinner on initial load, not during search
      if (!isSearch) {
        setIsLoading(true);
      }
      const response = await authService.searchUsers(searchParams);

      if (response.success && response.data) {
        setUsers(response.data);
        updateStatistics(response.data);
      } else {
        showError(response.error || 'Failed to load users');
        setUsers([]);
      }
    } catch (error) {
      showError('Failed to load users. Please try again.');
      setUsers([]);
    } finally {
      if (!isSearch) {
        setIsLoading(false);
      }
    }
  }, [searchParams, showError]);

  // Debounced search to prevent excessive API calls
  const debouncedLoadUsers = useCallback(() => {
    const timeoutId = setTimeout(() => {
      loadUsers(true); // Pass true to indicate this is a search operation
    }, 300); // 300ms debounce for better responsiveness

    return () => clearTimeout(timeoutId);
  }, [loadUsers]);

  // Update statistics
  const updateStatistics = (userData) => {
    const newStats = {
      totalUsers: userData.length,
      pendingUsers: userData.filter(user => !user.is_approved && user.is_email_verified).length,
      approvedUsers: userData.filter(user => user.is_approved).length,
      staffUsers: userData.filter(user => user.is_staff).length,
      superUsers: userData.filter(user => user.is_superuser).length,
      activeUsers: userData.filter(user => user.is_active).length
    };
    setStats(newStats);
  };

  // Load users on component mount
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    setSearchInput(searchParams.search || '');
  }, [searchParams.search]);

  // Debounced search when filter params change (not search input)
  useEffect(() => {
    // Only trigger search for filter changes, not search input changes
    const { search, ...filterParams } = searchParams;
    const hasFilterChanges = Object.values(filterParams).some(value => value !== '');

    if (hasFilterChanges) {
      const cleanup = debouncedLoadUsers();
      return cleanup;
    }
  }, [searchParams.is_approved, searchParams.is_staff, searchParams.is_superuser, searchParams.is_active, searchParams.is_email_verified, debouncedLoadUsers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle input changes
  // const handleInputChange = (field, value) => {
  //   setSearchParams(prev => ({
  //     ...prev,
  //     [field]: value
  //   }));
  // };

  // Search runs only on submit (search icon or Enter), matching Applications
  const handleSearchInputChange = useCallback((value) => {
    setSearchInput(value);
  }, []);

  const handleSearch = useCallback(() => {
    setSearchParams(prev => ({ ...prev, search: searchInput }));
  }, [searchInput]);


  // Apply quick filters with toggle functionality
  const applyQuickFilter = (filterType) => {
    setSearchParams(prev => {
      const newParams = { ...prev };

      switch (filterType) {
        case 'pending':
          if (prev.is_approved === 'false' && prev.is_email_verified === 'true') {
            // Toggle off
            newParams.is_approved = '';
            newParams.is_email_verified = '';
          } else {
            // Toggle on
            newParams.is_approved = 'false';
            newParams.is_email_verified = 'true';
          }
          break;
        case 'approved':
          if (prev.is_approved === 'true') {
            newParams.is_approved = '';
          } else {
            newParams.is_approved = 'true';
          }
          break;
        case 'staff':
          if (prev.is_staff === 'true') {
            newParams.is_staff = '';
          } else {
            newParams.is_staff = 'true';
          }
          break;
        case 'superuser':
          if (prev.is_superuser === 'true') {
            newParams.is_superuser = '';
          } else {
            newParams.is_superuser = 'true';
          }
          break;
        case 'active':
          if (prev.is_active === 'true') {
            newParams.is_active = '';
          } else {
            newParams.is_active = 'true';
          }
          break;
        case 'verified':
          if (prev.is_email_verified === 'true') {
            newParams.is_email_verified = '';
          } else {
            newParams.is_email_verified = 'true';
          }
          break;
        case 'all':
          // Clear all filters
          newParams.search = '';
          newParams.is_approved = '';
          newParams.is_staff = '';
          newParams.is_superuser = '';
          newParams.is_active = '';
          newParams.is_email_verified = '';
          break;
        default:
          // No action for unknown filter types
          break;
      }

      return newParams;
    });
  };


  // Handle user operations with enhanced error handling
  const handleCreateUser = async (userData) => {
    try {
      setIsProcessing(true);
      const response = await authService.createUser(userData);

      if (response.success) {
        showSuccess('User created successfully');
        setShowCreateUser(false);
        await loadUsers();
      } else {
        showError(response.error || 'Failed to create user');
      }
    } catch (error) {
      showError('Failed to create user. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdateUser = async (userId, userData) => {
    try {
      setIsProcessing(true);
      const response = await authService.updateUser(userId, userData);

      if (response.success) {
        showSuccess('User updated successfully');
        setShowEditUser(false);
        setSelectedUser(null);
        await loadUsers();
      } else {
        showError(response.error || response.message || 'Failed to update user');
      }
    } catch (error) {
      showError('Failed to update user. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        setIsProcessing(true);
        const response = await authService.deleteUser(userId);

        if (response.success) {
          showSuccess('User deleted successfully');
          setShowUserDetail(false);
          setSelectedUser(null);
          await loadUsers();
        } else {
          showError(response.error || 'Failed to delete user');
        }
      } catch (error) {
        showError('Failed to delete user. Please try again.');
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowUserDetail(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowEditUser(true);
  };



  // Handle user action (single or bulk)
  const handleUserAction = async () => {
    if (!selectedAction || selectedUsers.length === 0) {
      showError('Please select an action and at least one user');
      return;
    }

    try {
      setIsProcessing(true);

      // Handle view and edit actions (single user only)
      if (selectedAction === 'view' || selectedAction === 'edit') {
        if (selectedUsers.length > 1) {
          showError('View and Edit actions can only be performed on one user at a time');
          return;
        }

        const userId = selectedUsers[0];
        const user = users.find(u => u.id === userId);

        if (selectedAction === 'view') {
          setSelectedUser(user);
          setShowUserDetail(true);
        } else if (selectedAction === 'edit') {
          setSelectedUser(user);
          setShowEditUser(true);
        }

        setSelectedUsers([]);
        setSelectedAction('');
        setActionReason('');
        return;
      }

      // Handle single user actions vs bulk actions
      let response;
      let successMessage;

      if (selectedUsers.length === 1) {
        // Single user action
        const userId = selectedUsers[0];

        switch (selectedAction) {
          case 'approve':
            response = await authService.approveUser(userId, true);
            successMessage = 'User approved successfully';
            break;
          case 'reject':
            response = await authService.approveUser(userId, false);
            successMessage = 'User rejected successfully';
            break;
          case 'delete':
            response = await authService.deleteUser(userId);
            successMessage = 'User deleted successfully';
            break;
          default:
            // For other actions, use bulk API but with single user
            response = await authService.bulkUserAction(selectedUsers, selectedAction, actionReason);
            successMessage = `User ${selectedAction} completed successfully`;
        }
      } else {
        // Bulk user action
        response = await authService.bulkUserAction(selectedUsers, selectedAction, actionReason);
        successMessage = `Bulk ${selectedAction} completed successfully`;
      }

      if (response.success) {
        showSuccess(response.message || successMessage);
        await loadUsers();
      } else {
        const errorMessage = selectedUsers.length === 1
          ? `Failed to ${selectedAction} user`
          : `Failed to perform bulk ${selectedAction}`;
        showError(response.error || errorMessage);
      }

      setSelectedUsers([]);
      setSelectedAction('');
      setActionReason('');
    } catch (error) {
      showError('Failed to execute action');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle user selection
  const handleSelectUser = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user.id));
    }
  };

  // Get status badge
  const getStatusBadge = (user) => {
    if (!user.is_email_verified) {
      return <span className="status-badge unverified">Unverified</span>;
    }
    if (!user.is_approved) {
      return <span className="status-badge pending">Pending</span>;
    }
    return <span className="status-badge approved">Approved</span>;
  };

  // Get role badge
  const getRoleBadge = (user) => {
    if (user.is_superuser) {
      return <span className="status-badge superuser">Superuser</span>;
    }
    if (user.is_staff) {
      return <span className="status-badge staff">Staff</span>;
    }
    return <span className="status-badge regular">Regular</span>;
  };

  if (isLoading) {
    return (
      <div className="user-management">
        <div className="loading-overlay">
          <ProfessionalLoader message="Loading users..." size="large" variant="default" />
        </div>
      </div>
    );
  }

  return (
    <div className="user-management">
      {/* Users Management Section */}
      <div className="users-management-container">
        {/* Unified Header */}
        <UnifiedHeader
          title="User Management"
          subtitle="Manage and organize user accounts"
          icon="👥"
          count={users.length}
          countLabel="Total Users"
          selectedCount={selectedUsers.length}
          selectedLabel="Selected"
        >
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateUser(true)}
            disabled={isProcessing}
          >
            ➕ Create User
          </button>
        </UnifiedHeader>

        {/* Unified Statistics */}
        <UnifiedStats
          stats={[
            { title: '👥 Total Users', value: stats.totalUsers },
            { title: '⏳ Pending Approval', value: stats.pendingUsers },
            { title: '✅ Approved Users', value: stats.approvedUsers },
            { title: '👨‍💼 Staff Members', value: stats.staffUsers },
            { title: '👑 Superusers', value: stats.superUsers },
            { title: '🟢 Active Users', value: stats.activeUsers }
          ]}
        />

        {/* Unified Filters */}
        <UnifiedFilters
          searchValue={searchInput}
          onSearchChange={handleSearchInputChange}
          onSearchSubmit={handleSearch}
          quickFilters={getQuickFilters('users', searchParams)}
          onQuickFilterClick={applyQuickFilter}
        />


        {/* Unified Action Panel */}
        <UnifiedActionPanel
          selectedCount={selectedUsers.length}
          totalCount={users.length}
          selectedItemsLabel="users"
          onSelectAll={handleSelectAll}
          onClearSelection={() => setSelectedUsers([])}
          selectedAction={selectedAction}
          onActionChange={setSelectedAction}
          onExecuteAction={handleUserAction}
          isProcessing={isProcessing}
          actionOptions={[
            { value: 'view', label: '👁️ View Details', disabled: false },
            { value: 'edit', label: '✏️ Edit User', disabled: false },
            { value: 'approve', label: '✅ Approve Users', disabled: false },
            { value: 'reject', label: '❌ Reject Users', disabled: false },
            { value: 'delete', label: '🗑️ Delete Users', disabled: false }
          ]}
        >
          {/* Reason Field for Actions */}
          {(selectedAction === 'approve' || selectedAction === 'reject' ||
            selectedAction === 'delete') && (
              <div className="action-reason-container">
                <div className="reason-input-wrapper">
                  <input
                    type="text"
                    className="modern-reason-input"
                    placeholder="Enter reason for this action..."
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                    disabled={selectedUsers.length === 0}
                  />
                  <div className="input-icon">📝</div>
                </div>
              </div>
            )}
        </UnifiedActionPanel>

        {users.length > 0 ? (
          <UnifiedTable
            columns={[
              { key: 'id', header: 'ID', className: 'unified-table-select-header' },
              { key: 'email', header: 'Email' },
              { key: 'name', header: 'Name' },
              { key: 'role', header: 'Role' },
              { key: 'status', header: 'Status' },
              { key: 'verified', header: 'Verified' },
              { key: 'joined', header: 'Joined' }
            ]}
            data={users}
            onRowClick={handleViewUser}
            onSelectRow={handleSelectUser}
            selectedRows={selectedUsers}
            renderCell={(column, user) => {
              switch (column.key) {
                case 'id':
                  return <span className="unified-member-id">{user.id}</span>;
                case 'email':
                  return (
                    <a href={`mailto:${user.email}`} className="unified-email-link">
                      {user.email}
                    </a>
                  );
                case 'name':
                  return (
                    <div className="unified-contact-info">
                      <div className="unified-contact-name">
                        {user.first_name || user.last_name
                          ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                          : 'N/A'
                        }
                      </div>
                    </div>
                  );
                case 'role':
                  return getRoleBadge(user);
                case 'status':
                  return getStatusBadge(user);
                case 'verified':
                  return (
                    <span className={`unified-status-badge ${user.is_email_verified ? 'verified' : 'unverified'}`}>
                      {user.is_email_verified ? 'Yes' : 'No'}
                    </span>
                  );
                case 'joined':
                  return user.date_joined
                    ? new Date(user.date_joined).toLocaleDateString()
                    : 'N/A';
                default:
                  return user[column.key];
              }
            }}
          />
        ) : (
          <UnifiedEmptyState
            icon="👥"
            title="No users found"
            description="Try adjusting your search criteria or create a new user."
            actions={[
              {
                label: 'Create User',
                icon: '➕',
                onClick: () => setShowCreateUser(true),
                variant: 'primary'
              }
            ]}
          />
        )}
      </div>

      {/* Loading Overlay */}
      {isProcessing && (
        <div className="loading-overlay">
          <ProfessionalLoader message="Processing..." size="medium" variant="minimal" />
        </div>
      )}

      {/* User Create Modal */}
      {showCreateUser && (
        <UserCreateModal
          isOpen={showCreateUser}
          onClose={() => setShowCreateUser(false)}
          onSubmit={handleCreateUser}
          isProcessing={isProcessing}
          existingUsers={users}
        />
      )}

      {/* User Edit Modal */}
      {showEditUser && selectedUser && (
        <UserEditModal
          isOpen={showEditUser}
          onClose={() => {
            setShowEditUser(false);
            setSelectedUser(null);
          }}
          onSubmit={handleUpdateUser}
          user={selectedUser}
          isProcessing={isProcessing}
          existingUsers={users}
        />
      )}

      {/* User Detail Modal */}
      {showUserDetail && selectedUser && (
        <UserDetailModal
          isOpen={showUserDetail}
          onClose={() => {
            setShowUserDetail(false);
            setSelectedUser(null);
          }}
          user={selectedUser}
          onEdit={(user) => {
            setSelectedUser(user);
            setShowUserDetail(false);
            setShowEditUser(true);
          }}
          onDelete={handleDeleteUser}
          isProcessing={isProcessing}
        />
      )}
    </div>
  );
};

export default EnhancedUserManagement;